// PlanView - one level in plan, rooms coloured by use, the room-highlight
// system labelled, and the systems chosen above drawn on top. Plan north
// is up, matching the drawing sheets.
import React, { useMemo } from "react";
import {
  CATEGORY_FILL, ROOF, STATUS_COLOR, bounds, bucketKey, centroid, dotsPath, drawnSystems, linesPath, marksPath, pointsAttr,
  polysPath, roomLabel, systemColor, orthoPlan } from "./glanceModel";

export default function PlanView({ manifest, level, system, selectedRoomId, onSelectRoom, active, mode, statuses }) {
  const b = useMemo(() => bounds(manifest.levels || []), [manifest]);
  const scale = Math.min(1060 / b.w, 520 / b.h);
  // The building grid (guide 20.4): named bubbles along the top and the left,
  // the lines faint across the plan, so a room reads against the drawings.
  const grid = manifest.grid || {};
  const gx = Object.entries(grid.x || {}).filter(([, v]) => Number.isFinite(v) && v >= b.xmin - 5 && v <= b.xmin + b.w + 5);
  const gy = Object.entries(grid.y || {}).filter(([, v]) => Number.isFinite(v) && v >= b.ymin - 5 && v <= b.ymin + b.h + 5);
  const TOP = gx.length ? 30 : 0;
  const { P: toPlan, height } = orthoPlan(b, { scale, left: 40, bottom: 20, top: 20 + TOP });
  const Q = (p) => toPlan(p[0], p[1]);
  const proj = (x, y) => Q([x, y]);
  const drawn = drawnSystems(manifest).filter((s) => s.id !== ROOF && (!active || active.has(s.id)));
  const highlight = system && (!drawnSystems(manifest).length || !active || active.has(system.id)) ? system : null;
  const systemRooms = new Set((highlight && highlight.rooms) || []);
  const hasSystems = (manifest.systems || []).length > 0;
  const zones = manifest.zones || [];
  return (
    <svg viewBox={`0 0 1140 ${height}`} role="img" aria-label={`${level.name || level.id} floor plan with rooms coloured by use${gx.length ? ", on the building grid" : ""}`}>
      {level.footprint && <polygon points={pointsAttr(level.footprint, Q)} fill="none" stroke="var(--ink)" strokeWidth="2" />}
      {level.rooms.map((r) => {
        const inSystem = systemRooms.has(r.id);
        const fill = inSystem ? "var(--system)" : r.category === "tel" && hasSystems ? "var(--sup)" : CATEGORY_FILL[r.category] || "var(--circ)";
        return (
          <polygon key={r.id} className={`g-room${selectedRoomId === r.id ? " is-selected" : ""}`}
                   points={pointsAttr(r.polygon, Q)} fill={fill} fillOpacity={drawn.length && !inSystem ? 0.6 : 1}
                   stroke={inSystem ? "var(--systemink)" : "var(--line)"} strokeWidth={inSystem ? 1.5 : 0.6}
                   tabIndex={0} role="button" aria-label={roomLabel(r)}
                   onMouseEnter={() => onSelectRoom(r.id, true)} onFocus={() => onSelectRoom(r.id, true)}
                   onClick={() => onSelectRoom(r.id)} onKeyDown={(e) => { if (e.key === "Enter") onSelectRoom(r.id); }} />
        );
      })}
      {drawn.map((s) => zones.map((z) => {
        const key = bucketKey(level.id, s.id, z.index);
        const bucket = (manifest.buckets || {})[key];
        if (!bucket) return null;
        const st = mode === "progress" && statuses ? statuses[key] : null;
        const color = st ? STATUS_COLOR[st.st] : systemColor(s);
        return (
          <g key={key} style={{ pointerEvents: "none" }} opacity={mode === "progress" && !st ? 0.3 : 1}>
            {bucket.lines && <path d={linesPath(bucket.lines, proj)} stroke={color} strokeWidth={s.id === "ducts" ? 0.5 : 0.8} fill="none" strokeDasharray={s.dashed ? "3 2" : undefined} />}
            {bucket.polys && <path d={polysPath(bucket.polys, proj)} fill={color} />}
            {bucket.dots && <path d={dotsPath(bucket.dots, proj, 1.1)} fill={color} />}
            {bucket.marks && <path d={marksPath(bucket.marks, proj, 3.5)} fill={color} />}
          </g>
        );
      }))}
      <g className="g-grid" style={{ pointerEvents: "none" }} aria-hidden="true">
        {gx.map(([name, v]) => {
          const x = Q([v, b.ymin])[0];
          return (
            <g key={`x${name}`}>
              <line x1={x} y1={TOP + 4} x2={x} y2={height - 20} stroke="var(--ink2)" strokeWidth="0.6" strokeDasharray="4 6" opacity="0.45" />
              <circle cx={x} cy={14} r={10} fill="var(--sheet)" stroke="var(--ink2)" strokeWidth="1" />
              <text x={x} y={18} textAnchor="middle" fontSize={name.length > 2 ? 8 : 11} fill="var(--ink)">{name}</text>
            </g>
          );
        })}
        {gy.map(([name, v]) => {
          const y = Q([b.xmin, v])[1];
          return (
            <g key={`y${name}`}>
              <line x1={34} y1={y} x2={40 + b.w * scale} y2={y} stroke="var(--ink2)" strokeWidth="0.6" strokeDasharray="4 6" opacity="0.45" />
              <circle cx={20} cy={y} r={10} fill="var(--sheet)" stroke="var(--ink2)" strokeWidth="1" />
              <text x={20} y={y + 4} textAnchor="middle" fontSize={name.length > 2 ? 8 : 11} fill="var(--ink)">{name}</text>
            </g>
          );
        })}
      </g>
      {level.rooms.filter((r) => systemRooms.has(r.id)).map((r) => {
        const p = Q(centroid(r.polygon));
        return (
          <text key={r.id} x={p[0]} y={p[1] + 5} textAnchor="middle" fontSize="14" fontWeight="600" fill="var(--systemink)"
                paintOrder="stroke" stroke="var(--sheet)" strokeWidth={drawn.length ? 4 : 0} style={{ pointerEvents: "none" }}>
            {roomLabel(r, highlight)}
          </text>
        );
      })}
    </svg>
  );
}
